import { useEffect, useState } from 'react';
import { Film, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const CREDITS_PER_VIDEO = 235;
export const KEEP_STYLE = 'keep';

const StyleOption = ({ value, label, note, disabled, checked, onSelect }) => (
  <label
    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
      checked ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:bg-slate-50'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    data-testid={`video-style-option-${value}`}
  >
    <input type="radio" name="video-style" className="mt-1 accent-purple-600" checked={checked} disabled={disabled} onChange={() => onSelect(value)} />
    <div>
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      {note && <p className="text-xs text-slate-500">{note}</p>}
    </div>
  </label>
);

// Pick a video style before (re)generating. request = {title, count, allowKeep, defaultStyle}
export const VideoStyleDialog = ({ request, styles, onCancel, onConfirm }) => {
  const [choice, setChoice] = useState(KEEP_STYLE);

  useEffect(() => {
    if (request) setChoice(request.allowKeep ? KEEP_STYLE : (request.defaultStyle || 'coastal'));
  }, [request]);

  const selected = styles.find(s => s.key === choice);
  const blocked = choice !== KEEP_STYLE && selected && !selected.configured;
  const count = request?.count || 0;

  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="video-style-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Film className="h-5 w-5 text-purple-600" /> {request?.title}</DialogTitle>
          <DialogDescription>Choisissez le style vidéo. La nouvelle vidéo ira dans « Vidéos à approuver ».</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {request?.allowKeep && (
            <StyleOption value={KEEP_STYLE} label="Garder le style de chaque véhicule" note="Côtier par défaut" checked={choice === KEEP_STYLE} onSelect={setChoice} />
          )}
          {styles.map(s => (
            <StyleOption key={s.key} value={s.key} label={s.label} checked={choice === s.key} onSelect={setChoice}
              disabled={!s.configured} note={s.configured ? null : 'Aucune vidéo modèle — ajoutez-la dans « Styles vidéo »'} />
          ))}
        </div>
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="video-style-cost">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>~{CREDITS_PER_VIDEO} crédits PixVerse par vidéo{count > 1 ? ` (jusqu'à ~${count * CREDITS_PER_VIDEO} crédits pour ${count} véhicules)` : ''}. 2-5 min chacune. Les vidéos téléversées manuellement ne sont pas touchées.</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="video-style-cancel">Annuler</Button>
          <Button disabled={blocked} onClick={() => onConfirm(choice === KEEP_STYLE ? null : choice)} className="bg-purple-600 hover:bg-purple-700" data-testid="video-style-confirm">
            Générer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
